/**
 * The status vocabulary of the console, as data.
 *
 * Separated from `components/status.tsx` so the tone table and the message ids
 * are testable without a DOM, and so the component file exports only components.
 */

export type StatusDomain =
  | "merchant"
  | "kyb"
  | "kybCheck"
  | "subOrder"
  | "order"
  | "payoutBatch"
  | "payout"
  | "hold"
  | "dispute"
  | "return"
  | "issuer"
  | "catalog"
  | "unit"
  | "codShift"
  | "wmsWave"
  | "wmsTask"
  | "fraudCase"
  | "fraudDecision"
  | "fraudListing"
  | "ticket"
  | "ticketPriority"
  | "sla"
  | "feedback"
  | "npsBand"
  | "review"
  | "affiliatePartner"
  | "affiliateCommission"
  | "giftCard"
  | "pointLot"
  | "event"
  | "eventSession"
  | "scanResult";

export type Tone = "success" | "caution" | "critical" | "neutral";

const TONES: Readonly<Record<StatusDomain, Readonly<Record<string, Tone>>>> = {
  merchant: {
    draft: "neutral",
    submitted: "caution",
    in_review: "caution",
    approved: "success",
    rejected: "critical",
    suspended: "critical",
  },
  kyb: { approvable: "success", pending: "caution", needs_fix: "caution", blocked: "critical" },
  kybCheck: {
    pending: "caution",
    passed: "success",
    failed: "critical",
    // A sanctions HIT is a positive screening result and blocks approval
    // outright; a provider ERROR is us not knowing. They are never one colour.
    hit: "critical",
    error: "caution",
  },
  subOrder: {
    PENDING: "caution",
    CONFIRMED: "neutral",
    PICKING: "neutral",
    PACKED: "neutral",
    SHIPPED: "neutral",
    DELIVERED: "success",
    CANCELLED: "critical",
    RETURNED: "critical",
  },
  order: {
    PLACED: "neutral",
    AWAITING_PAYMENT: "caution",
    PAID: "neutral",
    IN_FULFILLMENT: "neutral",
    COMPLETED: "success",
    CANCELLED: "critical",
  },
  payoutBatch: {
    // DRAFT is caution, not neutral: a draft batch is money waiting on a person.
    DRAFT: "caution",
    APPROVED: "neutral",
    SENT: "neutral",
    RECONCILED: "success",
    FAILED: "critical",
  },
  payout: { PENDING: "caution", SENT: "neutral", RECONCILED: "success", FAILED: "critical" },
  hold: {
    UNDELIVERED: "caution",
    DISPUTED: "critical",
    RETURN_WINDOW: "neutral",
    KYB_PENDING: "caution",
    // A ticket line is never delivered: the money waits for the event itself (w5f-money).
    EVENT_PENDING: "neutral",
  },
  dispute: {
    OPEN: "caution",
    EVIDENCE_REQUIRED: "caution",
    UNDER_REVIEW: "caution",
    RESOLVED_BUYER: "success",
    RESOLVED_MERCHANT: "success",
    WITHDRAWN: "neutral",
  },
  return: {
    REQUESTED: "caution",
    APPROVED: "neutral",
    REJECTED: "critical",
    PICKUP_SCHEDULED: "neutral",
    RECEIVED: "neutral",
    INSPECTED: "neutral",
    REFUND_PENDING: "caution",
    REFUNDED: "success",
    CLOSED: "neutral",
    DISPUTED: "critical",
  },
  issuer: { pending: "caution", active: "success", suspended: "critical" },
  catalog: { draft: "neutral", active: "success", archived: "neutral" },
  unit: {
    RECEIVED: "neutral",
    ALLOCATED: "neutral",
    PICKING: "neutral",
    PACKED: "neutral",
    MANIFESTED: "neutral",
    IN_TRANSIT: "neutral",
    OUT_FOR_DELIVERY: "caution",
    DELIVERED: "success",
    // A failed attempt is not a lost parcel — it is a redelivery somebody has to
    // schedule, which is why it is caution rather than critical. RTO is the
    // parcel actually going back, and that IS a loss.
    FAILED: "caution",
    RTO: "critical",
    RETURNED: "critical",
    CANCELLED: "neutral",
  },
  codShift: {
    OPEN: "neutral",
    COUNTING: "caution",
    RECONCILED: "success",
    // A discrepant shift is cash that is not where the ledger says it is.
    DISCREPANT: "critical",
  },
  wmsWave: {
    // A wave still BUILDING has allocated no bins and nobody is walking for it;
    // that is a step in the process, not a warning. A CANCELLED wave, though, is
    // work somebody expected to go out today that will not.
    BUILDING: "neutral",
    RELEASED: "neutral",
    PICKING: "neutral",
    PICKED: "success",
    CANCELLED: "critical",
  },
  wmsTask: {
    // OPEN is caution rather than neutral on purpose: a released task nobody has
    // taken is the one state on this board that means somebody has to walk.
    OPEN: "caution",
    ASSIGNED: "neutral",
    DONE: "success",
    CANCELLED: "critical",
  },
  /**
   * A risk case. APPROVED is «we cleared this subject» and DECLINED is «we
   * refused it», so they are the success and the refusal of a decision that was
   * taken. ESCALATED is caution and NOT success: the module leaves
   * `fraud_case.status = 'OPEN'` behind for all three decisions, and an
   * escalation is a second operator's queue rather than a closed question.
   */
  fraudCase: { OPEN: "caution", APPROVED: "success", DECLINED: "critical", ESCALATED: "caution" },
  /**
   * What an assessment told its caller to do. ALLOW is **neutral, not success**:
   * it is the ordinary path through a checkout, and colouring the commonest row
   * on the register green would make the three that matter harder to find.
   */
  fraudDecision: { ALLOW: "neutral", REVIEW: "caution", HOLD: "caution", BLOCK: "critical" },
  /**
   * ALLOW is **caution, not success**, and this is the entry worth arguing. An
   * allow-list row is a rule somebody switched off for one subject — the shape
   * an insider or a mistake takes — so it is a thing to notice rather than a
   * reassurance. A BLOCK is the desk working as intended.
   */
  fraudListing: { BLOCK: "critical", ALLOW: "caution" },
  /**
   * A support ticket. PENDING_CUSTOMER is neutral because the clock is not ours
   * while we are waiting on the buyer; NEW and OPEN are both somebody's work.
   */
  ticket: {
    NEW: "caution",
    OPEN: "caution",
    PENDING_CUSTOMER: "neutral",
    RESOLVED: "success",
    CLOSED: "neutral",
  },
  ticketPriority: { LOW: "neutral", MEDIUM: "neutral", HIGH: "caution", URGENT: "critical" },
  /**
   * The SLA clock, computed at read time from `slaMinutesRemaining` rather than
   * stored. §11.10's polarity rule is why it colours at all: this is a metric
   * where a fall is unambiguously bad, so one colour can serve every row.
   */
  sla: { BREACHED: "critical", DUE_SOON: "caution", ON_TRACK: "success" },
  feedback: { NEW: "caution", ROUTED: "neutral", ACKNOWLEDGED: "neutral", CLOSED: "neutral" },
  npsBand: { DETRACTOR: "critical", PASSIVE: "neutral", PROMOTER: "success" },
  /**
   * REJECTED and HIDDEN are both refusals and both critical, but they are never
   * one word: REJECTED never reached a reader, while HIDDEN was live and was
   * taken down — the author already saw it published, which is what makes the
   * second a conversation and the first a queue item.
   */
  review: { PENDING: "caution", PUBLISHED: "success", REJECTED: "critical", HIDDEN: "critical" },
  affiliatePartner: {
    PENDING: "caution",
    ACTIVE: "success",
    SUSPENDED: "critical",
    REJECTED: "critical",
  },
  /**
   * APPROVED is caution for the reason a DRAFT payout batch is: it means «the
   * return window closed and this is now owed», which is money waiting on a
   * person. CLAWED_BACK is critical because a refund took the sale away after we
   * had already told the partner they had earned it.
   */
  affiliateCommission: {
    PENDING: "neutral",
    APPROVED: "caution",
    PAID: "success",
    REJECTED: "neutral",
    CLAWED_BACK: "critical",
  },
  /**
   * ISSUED is a minted code nobody has paid for; ACTIVE is one that has been
   * sold and whose balance is a liability on our books. VOID is the only one
   * that is a loss of face value somebody has to explain.
   */
  giftCard: {
    ISSUED: "neutral",
    ACTIVE: "success",
    DEPLETED: "neutral",
    EXPIRED: "neutral",
    VOID: "critical",
  },
  pointLot: { PENDING: "caution", CONFIRMED: "success", REVERSED: "critical" },
  event: {
    DRAFT: "neutral",
    PUBLISHED: "success",
    // SOLD_OUT is caution rather than success: it is good news commercially and
    // it is the state in which every further buyer is refused, which is what an
    // operator reading this register is deciding about.
    SOLD_OUT: "caution",
    CANCELLED: "critical",
    COMPLETED: "neutral",
  },
  eventSession: {
    SCHEDULED: "neutral",
    ON_SALE: "success",
    CLOSED: "neutral",
    CANCELLED: "critical",
    COMPLETED: "neutral",
  },
  /**
   * What happened at the door. Six of the seven are refusals and they are NOT
   * one colour: a DUPLICATE is the signal a QR has been shared and somebody has
   * to be spoken to, where NOT_YET_OPEN and CLOSED are the gate working exactly
   * as configured and a queue that is simply early or late.
   */
  scanResult: {
    ADMITTED: "success",
    DUPLICATE: "critical",
    INVALID: "critical",
    VOID: "critical",
    WRONG_SESSION: "caution",
    NOT_YET_OPEN: "neutral",
    CLOSED: "neutral",
  },
};

export function toneFor(domain: StatusDomain, code: string): Tone {
  return TONES[domain][code] ?? "neutral";
}

/**
 * The WORDS are not here.
 *
 * They live in `src/i18n/vocabulary.ts`, as literal `i18n._("…")` calls, because
 * `lingui extract` cannot see through the template literal a keyed lookup would
 * need — such a message never reaches the catalog, `catalogs.test.ts` cannot
 * notice it is untranslated, and the register renders the raw code. This module
 * is the TONE half of §2.4's «colour plus a word»; the vocabulary is the word.
 */
